import { Component, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { RBACService } from '../../../shared/rbac.service';
import { UtilsService } from '../../../shared/utils.service';
import { ApiTokensService, CreatedApiToken } from '../api-tokens.service';

@Component({
  selector: 'app-api-token-create-dialog',
  templateUrl: './api-token-create-dialog.component.html',
  styleUrls: ['./api-token-create-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ApiTokenCreateDialogComponent {
  // Form state.
  name: string = '';
  neverExpires: boolean = false;
  expiresAt: Date | null = null;

  // The minimum selectable expiry date is tomorrow (must be in the future).
  minDate: Date = new Date(new Date().setDate(new Date().getDate() + 1));

  // In-flight guard to prevent double submits.
  creating: boolean = false;

  // Reveal state: set once the token has been created. Holds the one-time plaintext.
  createdToken: CreatedApiToken | null = null;

  constructor(
    private dialogRef: MatDialogRef<ApiTokenCreateDialogComponent>,
    private apiTokens: ApiTokensService,
    public rbac: RBACService,
    public utils: UtilsService
  ) {}

  /**
   * Validate and create the token, then switch to the one-time reveal state.
   */
  onCreate(): void {
    if (this.creating) {
      return;
    }

    if (!this.name || !this.name.trim()) {
      this.utils.showToast('Please enter a name for the token.');
      return;
    }

    if (!this.neverExpires && !this.expiresAt) {
      this.utils.showToast(
        'Please choose an expiry date or select "Never expires".'
      );
      return;
    }

    this.creating = true;

    this.apiTokens
      .create({
        name: this.name.trim(),
        never_expires: this.neverExpires,
        expires_at:
          this.neverExpires || !this.expiresAt
            ? undefined
            : this.expiresAt.toISOString()
      })
      .then((token: CreatedApiToken) => {
        // Switch to the one-time reveal state.
        this.createdToken = token;
      })
      .catch((error: any) => {
        if (error && error.status === 403) {
          this.rbac.showGenericAccessDeniedMessage(
            'You do not have permission to create API tokens.'
          );
          this.dialogRef.close(false);
          return;
        }
        this.utils.handleAPIErrors(error);
      })
      .finally(() => {
        this.creating = false;
      });
  }

  /**
   * Close the dialog. Signals the parent to refresh the list when a token was created.
   */
  onClose(): void {
    this.dialogRef.close(!!this.createdToken);
  }
}
